import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { ThunkDispatch } from './GlobalMapSearch.poi';
import type {
  DebouncedSearchParams,
  MapCenter,
  SearchResultItem,
} from './GlobalMapSearch.types';
import { GIS_SEARCH_MIN_LENGTH } from './GlobalMapSearch.utils';
import { runGlobalMapSearch } from './search/orchestrator';
import type { SourceStatus } from './search/runtimeContext';

export type { SourceStatus } from './search/runtimeContext';

interface UseExecuteSearchArgs {
  mapCenterRef: React.MutableRefObject<MapCenter | undefined>;
  jurisdictionBoundaryRef: React.MutableRefObject<unknown>;
  isFocusedRef: React.MutableRefObject<boolean>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCursor: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * React state shell around the search orchestrator. Owns the in-flight
 * `AbortController`, exposes the streaming result list, and delegates the
 * actual classify → plan → fan-out work to `runGlobalMapSearch`.
 */
export default function useExecuteSearch({
  mapCenterRef,
  jurisdictionBoundaryRef,
  isFocusedRef,
  setIsOpen,
  setCursor,
}: UseExecuteSearchArgs) {
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchSources, setSearchSources] = useState<SourceStatus[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const thunkDispatch = useDispatch() as unknown as ThunkDispatch;

  const executeSearch = useCallback(
    async ({
      text,
      layers,
      axonDs,
      staticLayers,
      dynamicLayers,
      callQueue,
      transcriptions,
      messages,
      alerts,
      removedAlerts,
      alertAdrData,
      serviceUrl,
      token,
    }: DebouncedSearchParams) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (!text || text.length < GIS_SEARCH_MIN_LENGTH) {
        setResults([]);
        setIsSearching(false);
        return;
      }

      await runGlobalMapSearch({
        text,
        layers,
        axonDs,
        staticLayers,
        dynamicLayers,
        callQueue,
        transcriptions,
        messages,
        alerts,
        removedAlerts,
        alertAdrData,
        serviceUrl,
        token,
        signal: controller.signal,
        mapCenterRef,
        jurisdictionBoundaryRef,
        isFocusedRef,
        thunkDispatch,
        setResults,
        setIsSearching,
        setSearchSources,
        setIsOpen,
        setCursor,
      });
    },
    // Refs and stable dispatch only — intentionally identity-stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    results,
    setResults,
    isSearching,
    setIsSearching,
    searchSources,
    setSearchSources,
    abortRef,
    executeSearch,
  };
}
